import { useResolvedores } from '@/features/referencias/useReferencias';
import {
  rotuloDaSaudeDoPrazo,
  rotuloDaSituacao,
  situacoesEmOrdem,
  tokenDaSituacao,
  tokenDoPrazo,
} from '@/features/acoes/rotulos';
import type { ResumoDeAcoes, SaudePrazo, SituacaoAcao } from '@/types/api';

import estilos from './PainelDeResumo.module.css';

/**
 * Painel do topo da lista. Vem do protótipo `Painel de Melhorias CS`.
 *
 * Os quatro blocos do protótipo, na mesma ordem: contagem por status, composição em barra
 * única, "onde estão os itens" (por área) e "quem mais solicitou" (por cliente).
 *
 * O QUE MUDOU EM RELAÇÃO AO PROTÓTIPO
 *
 * O protótipo tinha um `Status` só, misturando onde a solicitação está com como está o
 * prazo: `ANDAMENTO` e `ATRASADO` disputavam a mesma lista, e um item atrasado deixava de
 * aparecer como em andamento. São duas perguntas diferentes, e aqui são duas linhas.
 *
 * Os tiles de situação FILTRAM a tabela abaixo; os de prazo não, porque a listagem não
 * aceita filtro por saúde do prazo. Tile que não faz nada ao ser clicado é pior do que tile
 * que nem convida ao clique, então os de prazo não são botões.
 */

interface Props {
  resumo: ResumoDeAcoes;
  /**
   * Situações que o painel admite mostrar.
   *
   * Os tiles são estas, e não as dez: no painel do Produto, um tile de Rascunho mostraria
   * um número que a tabela nunca traz, porque nenhum clique alcança rascunho ali.
   */
  situacoesDoPainel: SituacaoAcao[];
  /**
   * Destas, as que o painel NÃO abre por padrão.
   *
   * No Produto são as terminais: pertencem ao painel e são filtráveis, mas não entram na
   * fila. Sem marcá-las, o total do cartão não fecharia com a tabela e pareceria defeito.
   */
  situacoesForaDoPadrao: SituacaoAcao[];
  /** Situações selecionadas no filtro da tabela. */
  selecionadas: SituacaoAcao[];
  aoAlternarSituacao: (situacao: SituacaoAcao) => void;
  /** Saúdes do prazo selecionadas no filtro da tabela. */
  prazosSelecionados: SaudePrazo[];
  aoAlternarPrazo: (saude: SaudePrazo) => void;
  /** Clientes selecionados no filtro da tabela. */
  clientesSelecionados: number[];
  aoAlternarCliente: (clienteId: number) => void;
}

/** Ordem de leitura do prazo: o que exige ação primeiro. */
const prazosEmOrdem: SaudePrazo[] = [
  'Atrasado',
  'ProximoVencimento',
  'NoPrazo',
  'SemPrazo',
  'NaoAplicavel',
];

/** Quantas barras mostrar nas listas de área e cliente. */
const LIMITE_DE_BARRAS = 6;

export function PainelDeResumo({
  resumo,
  situacoesDoPainel,
  situacoesForaDoPadrao,
  selecionadas,
  aoAlternarSituacao,
  prazosSelecionados,
  aoAlternarPrazo,
  clientesSelecionados,
  aoAlternarCliente,
}: Props) {
  const { nomeDaArea, nomeDoCliente } = useResolvedores();

  const porSituacao = new Map(resumo.porSituacao.map((c) => [c.situacao, c.quantidade]));
  const porPrazo = new Map(resumo.porSaudeDoPrazo.map((c) => [c.saude, c.quantidade]));

  /*
    O total é o do PAINEL, não o do escopo.

    `resumo.total` conta tudo que o usuário pode ver. No painel do Produto isso inclui
    rascunho e encerrada, que ficam fora da fila — e o número não fecharia com a soma dos
    tiles nem com a contagem da tabela.
  */
  const tiles = situacoesEmOrdem.filter((s) => situacoesDoPainel.includes(s));
  const totalDoPainel = tiles.reduce((soma, s) => soma + (porSituacao.get(s) ?? 0), 0);

  // Com cliente filtrado o total pode ser 0 sem o escopo estar vazio; o painel precisa
  // continuar de pe para o cliente poder ser desmarcado.
  if (resumo.total === 0 && clientesSelecionados.length === 0) {
    return (
      <p className={estilos.vazio}>
        Nenhuma ação no seu escopo ainda. O painel aparece quando houver a primeira.
      </p>
    );
  }

  return (
    <div className={estilos.painel}>
      <section className={estilos.cartao} aria-label="Status das solicitações">
        <h2 className={estilos.titulo}>Status das solicitações</h2>
        <p className={estilos.apoio}>
          Clique para filtrar a tabela. {String(totalDoPainel)}{' '}
          {totalDoPainel === 1 ? 'ação' : 'ações'} neste painel
          {totalDoPainel === resumo.total ? '.' : `, de ${String(resumo.total)} no seu escopo.`}
        </p>

        <div className={estilos.tiles}>
          {/*
            As situações do painel aparecem todas, inclusive com zero: "nenhuma aguardando
            análise" é uma resposta, e some da tela se o tile só existir quando houver item.

            Mas as vazias ficam esmaecidas. Com dez situações e duas povoadas, dez tiles
            de peso igual fazem o olho procurar o que importa.
          */}
          {tiles.map((situacao) => {
            const quantidade = porSituacao.get(situacao) ?? 0;

            return (
              <button
                key={situacao}
                type="button"
                className={estilos.tile}
                style={{ ['--cor-do-tile' as string]: `var(${tokenDaSituacao[situacao]})` }}
                data-vazio={quantidade === 0}
                /* Contorno tracejado no que nao abre por padrao. E o que explica, sem
                   texto extra, por que a soma dos tiles passa da contagem da tabela. */
                data-fora-do-padrao={situacoesForaDoPadrao.includes(situacao)}
                title={
                  situacoesForaDoPadrao.includes(situacao)
                    ? `${rotuloDaSituacao[situacao]} nao abre nesta lista. Clique para incluir.`
                    : undefined
                }
                aria-pressed={selecionadas.includes(situacao)}
                onClick={() => {
                  aoAlternarSituacao(situacao);
                }}
              >
                <span className={estilos.numero}>{String(quantidade)}</span>
                <span className={estilos.rotulo}>{rotuloDaSituacao[situacao]}</span>
              </button>
            );
          })}
        </div>

        {/*
          A composição era um cartão separado, e virou uma faixa aqui.

          Ela responde a mesma pergunta dos tiles, em proporção em vez de número, e como
          cartão próprio ocupava uma linha inteira para mostrar uma tira de 14px. Colada
          nos tiles, ela lê como o resumo deles.

          aria-hidden porque não acrescenta informação: o número e o rótulo de cada
          situação estão logo acima.
        */}
        <div className={estilos.barra} aria-hidden="true">
          {tiles
            .filter((situacao) => (porSituacao.get(situacao) ?? 0) > 0)
            .map((situacao) => (
              <span
                key={situacao}
                className={estilos.fatia}
                style={{
                  width: `${String(((porSituacao.get(situacao) ?? 0) / totalDoPainel) * 100)}%`,
                  background: `var(${tokenDaSituacao[situacao]})`,
                }}
                title={`${rotuloDaSituacao[situacao]}: ${String(porSituacao.get(situacao) ?? 0)}`}
              />
            ))}
        </div>
      </section>

      {/* Três cartões de largura igual. A versão anterior misturava um cartão estreito
          com dois que atravessavam a grade, e sobrava uma coluna vazia em duas linhas. */}
      <div className={estilos.trio}>
        <section className={estilos.cartao} aria-label="Prazo">
          <h2 className={estilos.titulo}>Prazo</h2>
          <p className={estilos.apoio}>
            Clique para filtrar a tabela. Calculado a partir da data de hoje, a cada consulta.
          </p>

          <div className={estilos.prazos}>
            {prazosEmOrdem.map((saude) => {
              const quantidade = porPrazo.get(saude) ?? 0;

              return (
                <button
                  key={saude}
                  type="button"
                  className={estilos.prazo}
                  style={{ ['--cor-do-tile' as string]: `var(${tokenDoPrazo[saude]})` }}
                  data-vazio={quantidade === 0}
                  aria-pressed={prazosSelecionados.includes(saude)}
                  onClick={() => {
                    aoAlternarPrazo(saude);
                  }}
                >
                  <span className={estilos.rotulo}>{rotuloDaSaudeDoPrazo[saude]}</span>
                  <span className={estilos.numero}>{String(quantidade)}</span>
                </button>
              );
            })}
          </div>
        </section>

        <ListaDeBarras
          rotulo="Onde estão os itens"
          apoio="Por área responsável."
          itens={resumo.porArea.map((c) => ({
            chave: String(c.areaId),
            nome: nomeDaArea(c.areaId),
            quantidade: c.quantidade,
          }))}
        />

        <ListaDeBarras
          rotulo="Quem mais solicitou"
          apoio="Clique para filtrar a tabela. Por cliente afetado: uma ação pode afetar vários,
            então a soma passa do total."
          itens={resumo.porCliente.map((c) => ({
            chave: String(c.clienteId),
            nome: nomeDoCliente(c.clienteId),
            quantidade: c.quantidade,
          }))}
          selecionados={clientesSelecionados.map(String)}
          aoAlternar={(chave) => {
            aoAlternarCliente(Number(chave));
          }}
        />
      </div>
    </div>
  );
}

interface ItemDeBarra {
  chave: string;
  nome: string;
  quantidade: number;
}

interface ListaDeBarrasProps {
  rotulo: string;
  apoio: string;
  itens: ItemDeBarra[];
  /**
   * Chaves selecionadas, e callback de alternância.
   *
   * OPCIONAIS: "Onde estão os itens" (área responsável) não é filtro, só leitura — não há
   * filtro de área responsável na tabela do painel, e a lista inteira viraria clicável sem
   * fazer nada. Quando ausentes, a lista renderiza como antes, sem `<button>`.
   */
  selecionados?: string[];
  aoAlternar?: (chave: string) => void;
}

/**
 * Lista de barras horizontais.
 *
 * A barra é proporcional ao MAIOR da lista, não ao total: com 20 clientes, a maior fatia
 * seria 15% do total e todas as barras ficariam invisíveis. O número ao lado é o dado; a
 * barra é a comparação entre eles.
 */
function ListaDeBarras({ rotulo, apoio, itens, selecionados, aoAlternar }: ListaDeBarrasProps) {
  const clicavel = aoAlternar !== undefined;

  /*
    Clicavel mostra TODOS, com scroll. Nao-clicavel continua cortando em
    `LIMITE_DE_BARRAS` com "e mais N itens": "onde estao os itens" (area) nao e filtro,
    e uma lista inteira so para leitura nao justifica scroll dentro do cartao.

    "Quem mais solicitou" e filtro — cortar nos 6 primeiros escondia o cliente que a
    pessoa queria marcar, sem nenhum jeito de chegar nele.
  */
  const visiveis = clicavel ? itens : itens.slice(0, LIMITE_DE_BARRAS);
  const maior = itens[0]?.quantidade ?? 0;
  const restantes = itens.length - visiveis.length;

  return (
    <section className={estilos.cartao} aria-label={rotulo}>
      <h2 className={estilos.titulo}>{rotulo}</h2>
      <p className={estilos.apoio}>{apoio}</p>

      {visiveis.length === 0 ? (
        <p className={estilos.apoio}>Sem dados.</p>
      ) : (
        <ul className={clicavel ? `${estilos.barras} ${estilos.barrasComScroll}` : estilos.barras}>
          {visiveis.map((item) => {
            const largura = maior === 0 ? '0%' : `${String((item.quantidade / maior) * 100)}%`;

            // Mesmo conteudo visual nos dois casos: so a tag muda, para nao duplicar o
            // JSX inteiro num `if` separado.
            return (
              <li key={item.chave} className={estilos.linha}>
                {clicavel ? (
                  <button
                    type="button"
                    className={estilos.linhaClicavel}
                    aria-pressed={selecionados?.includes(item.chave) ?? false}
                    onClick={() => {
                      aoAlternar(item.chave);
                    }}
                  >
                    <span className={estilos.nome} title={item.nome}>
                      {item.nome}
                    </span>
                    <span className={estilos.quantidade}>{String(item.quantidade)}</span>
                    <span className={estilos.trilha}>
                      <span className={estilos.preenchimento} style={{ width: largura }} />
                    </span>
                  </button>
                ) : (
                  <>
                    <span className={estilos.nome} title={item.nome}>
                      {item.nome}
                    </span>
                    <span className={estilos.quantidade}>{String(item.quantidade)}</span>
                    <span className={estilos.trilha}>
                      <span className={estilos.preenchimento} style={{ width: largura }} />
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {restantes > 0 && (
        <p className={estilos.apoio}>
          e mais {String(restantes)} {restantes === 1 ? 'item' : 'itens'} com menos ações.
        </p>
      )}
    </section>
  );
}
